import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult, CNetHintBar, G } from '../other/_cnet';

const MODES = {
  'Round half up': (n, p) => { const m = 10 ** p; return Math.sign(n) * Math.round(Math.abs(n) * m) / m; },
  'Round half down': (n, p) => { const m = 10 ** p; const r = Math.abs(n) * m; const f = Math.floor(r); return Math.sign(n) * ((r - f === 0.5 ? f : Math.round(r)) / m); },
  'Round half to even (banker\'s)': (n, p) => { const m = 10 ** p; const r = n * m; const rounded = Math.round(r); return (Math.abs(r - Math.trunc(r)) === 0.5 && Math.trunc(r) % 2 === 0) ? Math.trunc(r) / m : rounded / m; },
  'Round half away from zero': (n, p) => { const m = 10 ** p; return Math.sign(n) * Math.ceil(Math.abs(n) * m - 0.5 + Number.EPSILON) / m; }, // approx
  'Round up (ceil)': (n, p) => { const m = 10 ** p; return Math.ceil(n * m) / m; },
  'Round down (floor)': (n, p) => { const m = 10 ** p; return Math.floor(n * m) / m; },
  'Truncate': (n, p) => { const m = 10 ** p; return Math.trunc(n * m) / m; },
};

const PRECISION_PRESETS = [
  { label: 'Ones (1)', value: 0 },
  { label: 'Tens (10)', value: -1 },
  { label: 'Hundreds (100)', value: -2 },
  { label: 'Thousands (1,000)', value: -3 },
  { label: 'Ten thousands (10,000)', value: -4 },
  { label: 'Hundred thousands (100,000)', value: -5 },
  { label: 'Millions (1,000,000)', value: -6 },
  { label: 'Tenths (0.1)', value: 1 },
  { label: 'Hundredths (0.01)', value: 2 },
  { label: 'Thousandths (0.001)', value: 3 },
  { label: 'Ten-thousandths (0.0001)', value: 4 },
  { label: 'Hundred-thousandths (0.00001)', value: 5 },
  { label: 'Millionths (0.000001)', value: 6 },
];

export default function RoundingCalc() {
  const [num, setNum] = useState(3.14159265358979);
  const [precision, setPrec] = useState(2);
  const [mode, setMode] = useState('Round half up');
  const [res, setRes] = useState(null);
  const calc = () => {
    const n = Number(num);
    const rows = Object.entries(MODES).map(([k, fn]) => ({ mode: k, value: fn(n, Number(precision)) }));
    setRes({ n, primary: MODES[mode](n, Number(precision)), rows });
  };
  const clear = () => { setNum(3.14159265358979); setPrec(2); setMode('Round half up'); setRes(null); };
  return (
    <CalcShell showActions={false} look="cnet" title="Rounding Calculator" subtitle="Round a number to N decimal places using half-up, half-down, banker's, ceil, floor, or truncate modes.">
      <CNetHintBar />
      <div className="cnet-panel">
        <CNetRow label="Number">
          <input data-testid="rnd-num" className="cnet-input" type="number" step="any" value={num} onChange={e => setNum(e.target.value)} />
          <span />
        </CNetRow>
        <CNetRow label="Round to (decimal places)">
          <input data-testid="rnd-p" className="cnet-input" type="number" step="1" value={precision} onChange={e => setPrec(e.target.value)} />
          <span />
        </CNetRow>
        <div className="cnet-row-2col"><div className="cnet-label">Precision preset</div>
          <select data-testid="rnd-preset" className="cnet-select" onChange={e => setPrec(Number(e.target.value))} value={String(precision)}>
            {PRECISION_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div className="cnet-row-2col"><div className="cnet-label">Rounding mode</div>
          <select data-testid="rnd-mode" className="cnet-select" value={mode} onChange={e => setMode(e.target.value)}>
            {Object.keys(MODES).map(k => <option key={k}>{k}</option>)}
          </select>
        </div>
        <CNetActions onCalc={calc} onClear={clear} testId="rnd" />
      </div>
      {res && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="rnd-result">
            {res.n} → <span className="text-[#4c7b25]">{res.primary}</span> <span className="text-sm text-slate-600">({mode})</span>
          </div>
          <div className="mt-3 font-bold text-slate-900">All rounding modes:</div>
          <table className="data-table" data-testid="rnd-table">
            <thead><tr><th>Mode</th><th>Result</th></tr></thead>
            <tbody>
              {res.rows.map(r => <tr key={r.mode}><td>{r.mode}</td><td className="font-mono">{r.value}</td></tr>)}
            </tbody>
          </table>
        </CNetResult>
      )}
    </CalcShell>
  );
}
